import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { contentApi } from '@/services/contentApi';

// ============================================================================
// Types
// ============================================================================

export interface EditorTab {
  id: string;              // Content item ID (or itemId-vN for version tabs)
  name: string;            // Display name
  contentType: string;     // agent, prompt, skill, etc.
  content: string;         // Current content in editor
  originalContent: string; // For dirty detection
  isDirty: boolean;        // Has unsaved changes
  isLoading: boolean;      // Loading content
  folderId?: string;       // Folder ID for context
  isReadOnly?: boolean;    // True for version preview tabs
  versionNumber?: number;  // Which version is being viewed
  sourceItemId?: string;   // Original item ID (for linking back)
}

export interface TabMetadata {
  id: string;
  name: string;
  contentType: string;
  folderId?: string;
}

interface EditorTabsState {
  tabs: EditorTab[];
  activeTabId: string | null;
  maxTabs: number;
}

// ============================================================================
// Actions
// ============================================================================

type Action =
  | { type: 'SET_TABS'; payload: EditorTab[] }
  | { type: 'ADD_TAB'; payload: EditorTab }
  | { type: 'REMOVE_TAB'; payload: string }
  | { type: 'SET_ACTIVE_TAB'; payload: string | null }
  | { type: 'UPDATE_TAB_CONTENT'; payload: { id: string; content: string } }
  | { type: 'SET_TAB_ORIGINAL_CONTENT'; payload: { id: string; content: string } }
  | { type: 'SET_TAB_LOADING'; payload: { id: string; loading: boolean } }
  | { type: 'UPDATE_TAB_METADATA'; payload: { id: string; name?: string; contentType?: string; content?: string } }
  | { type: 'REVERT_TAB'; payload: string }
  | { type: 'CLOSE_OTHER_TABS'; payload: string }
  | { type: 'CLOSE_ALL_TABS' };

// ============================================================================
// Initial State
// ============================================================================

const initialState: EditorTabsState = {
  tabs: [],
  activeTabId: null,
  maxTabs: 15,
};

// ============================================================================
// Reducer
// ============================================================================

function reducer(state: EditorTabsState, action: Action): EditorTabsState {
  switch (action.type) {
    case 'SET_TABS':
      return { ...state, tabs: action.payload };

    case 'ADD_TAB': {
      // Check if tab already exists
      const existingIndex = state.tabs.findIndex(t => t.id === action.payload.id);
      if (existingIndex >= 0) {
        // Tab exists, just make it active
        return { ...state, activeTabId: action.payload.id };
      }

      // Check max tabs limit - remove oldest non-dirty tab if needed
      let newTabs = [...state.tabs];
      if (newTabs.length >= state.maxTabs) {
        // Find first non-dirty tab that isn't active
        const removeIndex = newTabs.findIndex(
          t => !t.isDirty && t.id !== state.activeTabId
        );
        if (removeIndex >= 0) {
          newTabs.splice(removeIndex, 1);
        } else {
          // All tabs are dirty or active, remove the oldest anyway
          newTabs.shift();
        }
      }

      return {
        ...state,
        tabs: [...newTabs, action.payload],
        activeTabId: action.payload.id,
      };
    }

    case 'REMOVE_TAB': {
      const newTabs = state.tabs.filter(t => t.id !== action.payload);
      let newActiveId = state.activeTabId;

      // If we're removing the active tab, switch to another
      if (state.activeTabId === action.payload) {
        const removedIndex = state.tabs.findIndex(t => t.id === action.payload);
        if (newTabs.length > 0) {
          // Try to activate the tab to the right, or the one to the left
          newActiveId = newTabs[Math.min(removedIndex, newTabs.length - 1)]?.id || null;
        } else {
          newActiveId = null;
        }
      }

      return { ...state, tabs: newTabs, activeTabId: newActiveId };
    }

    case 'SET_ACTIVE_TAB':
      return { ...state, activeTabId: action.payload };

    case 'UPDATE_TAB_CONTENT': {
      return {
        ...state,
        tabs: state.tabs.map(t =>
          t.id === action.payload.id
            ? {
                ...t,
                content: action.payload.content,
                isDirty: action.payload.content !== t.originalContent,
              }
            : t
        ),
      };
    }

    case 'SET_TAB_ORIGINAL_CONTENT': {
      return {
        ...state,
        tabs: state.tabs.map(t =>
          t.id === action.payload.id
            ? {
                ...t,
                originalContent: action.payload.content,
                content: action.payload.content,
                isDirty: false,
                isLoading: false,
              }
            : t
        ),
      };
    }

    case 'SET_TAB_LOADING': {
      return {
        ...state,
        tabs: state.tabs.map(t =>
          t.id === action.payload.id
            ? { ...t, isLoading: action.payload.loading }
            : t
        ),
      };
    }

    case 'REVERT_TAB': {
      return {
        ...state,
        tabs: state.tabs.map(t =>
          t.id === action.payload
            ? { ...t, content: t.originalContent, isDirty: false }
            : t
        ),
      };
    }

    case 'UPDATE_TAB_METADATA': {
      const { id, name, contentType, content } = action.payload;
      const tabExists = state.tabs.some(t => t.id === id);

      if (!tabExists) {
        // Tab was removed during loading, don't add it back
        return state;
      }

      return {
        ...state,
        tabs: state.tabs.map(t =>
          t.id === id
            ? {
                ...t,
                name: name ?? t.name,
                contentType: contentType ?? t.contentType,
                content: content ?? t.content,
                originalContent: content ?? t.originalContent,
                isDirty: false,
                isLoading: false,
              }
            : t
        ),
      };
    }

    case 'CLOSE_OTHER_TABS': {
      const tabToKeep = state.tabs.find(t => t.id === action.payload);
      return {
        ...state,
        tabs: tabToKeep ? [tabToKeep] : [],
        activeTabId: action.payload,
      };
    }

    case 'CLOSE_ALL_TABS':
      return { ...state, tabs: [], activeTabId: null };

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

interface EditorTabsContextValue {
  // State
  tabs: EditorTab[];
  activeTabId: string | null;
  activeTab: EditorTab | undefined;

  // Tab management
  openTab: (itemId: string, metadata?: Partial<TabMetadata>) => Promise<void>;
  openVersionTab: (itemId: string, name: string, versionNumber: number, content: string, contentType: string) => void;
  closeTab: (tabId: string, force?: boolean) => Promise<boolean>;
  setActiveTab: (tabId: string) => void;

  // Content management
  updateTabContent: (tabId: string, content: string) => void;
  saveTab: (tabId: string) => Promise<void>;
  revertTab: (tabId: string) => void;
  markTabSaved: (tabId: string, content: string) => void;

  // Bulk operations
  closeOtherTabs: (tabId: string) => Promise<boolean>;
  closeAllTabs: () => Promise<boolean>;

  // Utilities
  hasUnsavedChanges: () => boolean;
  getTabById: (tabId: string) => EditorTab | undefined;
}

const EditorTabsContext = createContext<EditorTabsContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface EditorTabsProviderProps {
  children: ReactNode;
  storageKey?: string;  // Different keys for Editor vs Playground
  onSaveTab?: (tabId: string, content: string) => Promise<void>;
}

export function EditorTabsProvider({
  children,
  storageKey = 'editor_tabs_state',
  onSaveTab,
}: EditorTabsProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Ref to track current tabs - avoids stale closure issues in callbacks
  const tabsRef = React.useRef(state.tabs);
  tabsRef.current = state.tabs;

  // -------------------------------------------------------------------------
  // localStorage persistence
  // -------------------------------------------------------------------------

  // Load tabs from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as {
          tabs: TabMetadata[];
          activeTabId: string | null;
        };

        // Restore tabs with minimal metadata (content loaded lazily)
        const restoredTabs: EditorTab[] = parsed.tabs.slice(0, 10).map(meta => ({
          id: meta.id,
          name: meta.name,
          contentType: meta.contentType,
          folderId: meta.folderId,
          content: '',
          originalContent: '',
          isDirty: false,
          isLoading: true,
        }));

        dispatch({ type: 'SET_TABS', payload: restoredTabs });

        // Set active tab
        if (parsed.activeTabId && restoredTabs.some(t => t.id === parsed.activeTabId)) {
          dispatch({ type: 'SET_ACTIVE_TAB', payload: parsed.activeTabId });
        } else if (restoredTabs.length > 0) {
          dispatch({ type: 'SET_ACTIVE_TAB', payload: restoredTabs[0].id });
        }

        // Load content for restored tabs (fetch published version)
        restoredTabs.forEach(async (tab) => {
          try {
            const content = await contentApi.getContent(tab.id, null, null, true); // published=true
            dispatch({
              type: 'SET_TAB_ORIGINAL_CONTENT',
              payload: { id: tab.id, content },
            });
          } catch (error) {
            console.error(`Failed to load content for tab ${tab.id}:`, error);
            // Remove tab if content can't be loaded
            dispatch({ type: 'REMOVE_TAB', payload: tab.id });
          }
        });
      }
    } catch (error) {
      console.error('Failed to restore tabs from localStorage:', error);
    }
  }, [storageKey]);

  // Save tabs to localStorage (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        const toStore = {
          tabs: state.tabs.map(t => ({
            id: t.id,
            name: t.name,
            contentType: t.contentType,
            folderId: t.folderId,
          })),
          activeTabId: state.activeTabId,
        };
        localStorage.setItem(storageKey, JSON.stringify(toStore));
      } catch (error) {
        console.error('Failed to save tabs to localStorage:', error);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [state.tabs, state.activeTabId, storageKey]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const openTab = useCallback(async (itemId: string, metadata?: Partial<TabMetadata>) => {
    // Create tab object - the ADD_TAB reducer handles existing tabs correctly
    // by just activating them instead of duplicating
    const newTab: EditorTab = {
      id: itemId,
      name: metadata?.name || 'Loading...',
      contentType: metadata?.contentType || 'unknown',
      folderId: metadata?.folderId,
      content: '',
      originalContent: '',
      isDirty: false,
      isLoading: true,
    };

    // Check if tab already exists using ref (always current)
    const existingTab = tabsRef.current.find(t => t.id === itemId);
    if (existingTab) {
      // Tab exists, just activate it
      dispatch({ type: 'SET_ACTIVE_TAB', payload: itemId });
      // If tab has content, we're done. If still loading, let it finish.
      if (existingTab.content !== '' || existingTab.isLoading) {
        return;
      }
      // Tab exists but has no content and isn't loading - reload it
    } else {
      // Tab doesn't exist, add it
      dispatch({ type: 'ADD_TAB', payload: newTab });
    }

    // Fetch item details and published content (falls back to latest if none published)
    try {
      const [item, content] = await Promise.all([
        contentApi.getItem(itemId),
        contentApi.getContent(itemId, null, null, true), // published=true
      ]);

      // Update tab with loaded data and metadata
      // Use UPDATE_TAB_METADATA action to avoid stale closure issues with state.tabs
      dispatch({
        type: 'UPDATE_TAB_METADATA',
        payload: {
          id: itemId,
          name: item?.name,
          contentType: item?.content_type,
          content,
        },
      });
    } catch (error) {
      console.error(`Failed to load tab ${itemId}:`, error);
      dispatch({ type: 'REMOVE_TAB', payload: itemId });
      throw error;
    }
  }, []); // No dependencies - uses tabsRef which is always current

  const openVersionTab = useCallback((
    itemId: string,
    name: string,
    versionNumber: number,
    content: string,
    contentType: string
  ) => {
    const tabId = `${itemId}-v${versionNumber}`;

    // Check if this version tab already exists
    const existingTab = tabsRef.current.find(t => t.id === tabId);
    if (existingTab) {
      // Tab exists, just activate it
      dispatch({ type: 'SET_ACTIVE_TAB', payload: tabId });
      return;
    }

    // Create a new read-only tab for this version
    const versionTab: EditorTab = {
      id: tabId,
      name: `${name} (v${versionNumber})`,
      contentType,
      content,
      originalContent: content,
      isDirty: false,
      isLoading: false,
      isReadOnly: true,
      versionNumber,
      sourceItemId: itemId,
    };

    dispatch({ type: 'ADD_TAB', payload: versionTab });
  }, []);

  const closeTab = useCallback(async (tabId: string, force = false): Promise<boolean> => {
    const tab = state.tabs.find(t => t.id === tabId);
    if (!tab) return true;

    // Check for unsaved changes
    if (tab.isDirty && !force) {
      const confirmed = window.confirm(
        `"${tab.name}" has unsaved changes. Close anyway?`
      );
      if (!confirmed) return false;
    }

    dispatch({ type: 'REMOVE_TAB', payload: tabId });
    return true;
  }, [state.tabs]);

  const setActiveTab = useCallback((tabId: string) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tabId });
  }, []);

  const updateTabContent = useCallback((tabId: string, content: string) => {
    dispatch({ type: 'UPDATE_TAB_CONTENT', payload: { id: tabId, content } });
  }, []);

  const saveTab = useCallback(async (tabId: string) => {
    const tab = state.tabs.find(t => t.id === tabId);
    if (!tab || !tab.isDirty) return;

    if (onSaveTab) {
      await onSaveTab(tabId, tab.content);
    } else {
      // Default save behavior - save as draft
      await contentApi.saveDraft(tabId, tab.content);
    }

    dispatch({
      type: 'SET_TAB_ORIGINAL_CONTENT',
      payload: { id: tabId, content: tab.content },
    });
  }, [state.tabs, onSaveTab]);

  const revertTab = useCallback((tabId: string) => {
    dispatch({ type: 'REVERT_TAB', payload: tabId });
  }, []);

  const markTabSaved = useCallback((tabId: string, content: string) => {
    dispatch({
      type: 'SET_TAB_ORIGINAL_CONTENT',
      payload: { id: tabId, content },
    });
  }, []);

  const closeOtherTabs = useCallback(async (tabId: string): Promise<boolean> => {
    const otherTabs = state.tabs.filter(t => t.id !== tabId);
    const dirtyTabs = otherTabs.filter(t => t.isDirty);

    if (dirtyTabs.length > 0) {
      const confirmed = window.confirm(
        `${dirtyTabs.length} file(s) have unsaved changes. Close them anyway?`
      );
      if (!confirmed) return false;
    }

    dispatch({ type: 'CLOSE_OTHER_TABS', payload: tabId });
    return true;
  }, [state.tabs]);

  const closeAllTabs = useCallback(async (): Promise<boolean> => {
    const dirtyTabs = state.tabs.filter(t => t.isDirty);

    if (dirtyTabs.length > 0) {
      const confirmed = window.confirm(
        `${dirtyTabs.length} file(s) have unsaved changes. Close them anyway?`
      );
      if (!confirmed) return false;
    }

    dispatch({ type: 'CLOSE_ALL_TABS' });
    return true;
  }, [state.tabs]);

  const hasUnsavedChanges = useCallback(() => {
    return state.tabs.some(t => t.isDirty);
  }, [state.tabs]);

  const getTabById = useCallback((tabId: string) => {
    return state.tabs.find(t => t.id === tabId);
  }, [state.tabs]);

  // -------------------------------------------------------------------------
  // Computed values
  // -------------------------------------------------------------------------

  const activeTab = useMemo(() => {
    return state.tabs.find(t => t.id === state.activeTabId);
  }, [state.tabs, state.activeTabId]);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------

  const value = useMemo<EditorTabsContextValue>(() => ({
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    activeTab,
    openTab,
    openVersionTab,
    closeTab,
    setActiveTab,
    updateTabContent,
    saveTab,
    revertTab,
    markTabSaved,
    closeOtherTabs,
    closeAllTabs,
    hasUnsavedChanges,
    getTabById,
  }), [
    state.tabs,
    state.activeTabId,
    activeTab,
    openTab,
    openVersionTab,
    closeTab,
    setActiveTab,
    updateTabContent,
    saveTab,
    revertTab,
    markTabSaved,
    closeOtherTabs,
    closeAllTabs,
    hasUnsavedChanges,
    getTabById,
  ]);

  return (
    <EditorTabsContext.Provider value={value}>
      {children}
    </EditorTabsContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useEditorTabs(): EditorTabsContextValue {
  const context = useContext(EditorTabsContext);
  if (!context) {
    throw new Error('useEditorTabs must be used within an EditorTabsProvider');
  }
  return context;
}

// ============================================================================
// Exports
// ============================================================================

export default EditorTabsContext;
